import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/shared/components/ui/field';
import { Input } from '@/shared/components/ui/input';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FormFieldSelect } from '@/shared/components/custom/form-field/FormFieldSelect';
import { ColorPickerPopover } from '@/shared/components/custom/color-picker/color-picker.index';
import { getPaletteColor } from '@/core/lib/colors';
import { useToast } from '@/shared/hooks/useToast';
import { boardStatusSchema, type BoardStatusFormValues } from '../schemas';
import { useCreateBoardStatus, useUpdateBoardStatus } from '../hooks/useBoardStatusMutations';
import type { BoardStatus, StatusType } from '../types';

interface BoardStatusFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boardId: string;
  status?: BoardStatus;
}

const STATUS_TYPE_OPTIONS = (t: (key: string) => string) => [
  { value: 'Todo', label: t('statuses.types.todo') },
  { value: 'InProgress', label: t('statuses.types.inProgress') },
  { value: 'Done', label: t('statuses.types.done') },
];

export function BoardStatusFormDialog({
  open,
  onOpenChange,
  boardId,
  status,
}: BoardStatusFormDialogProps) {
  const { t } = useTranslation('boards');
  const { toast } = useToast();
  const isEditing = !!status;

  const createStatus = useCreateBoardStatus(boardId);
  const updateStatus = useUpdateBoardStatus(boardId);
  const isPending = createStatus.isPending || updateStatus.isPending;

  const { control, handleSubmit, reset } = useForm<BoardStatusFormValues>({
    resolver: zodResolver(boardStatusSchema),
    defaultValues: { name: '', color: '', type: 'Todo', wipLimit: undefined },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: status?.name ?? '',
        color: status?.color ?? '',
        type: (status?.type as StatusType) ?? 'Todo',
        wipLimit: status?.wipLimit ?? undefined,
      });
    }
  }, [open, status, reset]);

  function onSubmit(data: BoardStatusFormValues) {
    const payload = {
      name: data.name,
      color: data.color,
      type: data.type,
      wipLimit: data.wipLimit ?? null,
    };

    if (isEditing && status?.id) {
      updateStatus.mutate(
        { statusId: status.id, data: payload },
        {
          onSuccess: () => {
            toast.success({ title: t('statuses.toast.updateSuccess') });
            onOpenChange(false);
          },
        },
      );
    } else {
      createStatus.mutate(payload, {
        onSuccess: () => {
          toast.success({ title: t('statuses.toast.createSuccess') });
          onOpenChange(false);
        },
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full rounded-none md:max-w-[440px] md:rounded-lg"
        data-testid="boards-status-form-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('statuses.actions.edit') : t('statuses.actions.create')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t('dialogs.boardStatusFormDescription')}
          </DialogDescription>
        </DialogHeader>

        <form
          id="board-status-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 py-2"
        >
          {/* Color picker + name row */}
          <div className="flex items-end gap-3">
            <Controller
              control={control}
              name="color"
              render={({ field, fieldState }) => {
                const colorValue = field.value ? getPaletteColor(field.value)?.value : undefined;
                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel>{t('statuses.form.color')}</FieldLabel>
                    <ColorPickerPopover
                      value={field.value}
                      onSelect={(color) => field.onChange(color)}
                      trigger={
                        <Button
                          type="button"
                          variant="outline"
                          className="h-10 w-10 p-0"
                          disabled={isPending}
                          data-testid="boards-status-form-color-input"
                        >
                          <div
                            className="h-6 w-6 rounded-full border border-border"
                            style={colorValue ? { background: colorValue } : undefined}
                          />
                        </Button>
                      }
                    />
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </Field>
                );
              }}
            />

            <FormFieldInput
              control={control}
              name="name"
              label={t('statuses.form.name')}
              floatingLabel
              disabled={isPending}
              data-testid="boards-status-form-name-input"
            />
          </div>

          <FormFieldSelect
            control={control}
            name="type"
            label={t('statuses.form.type')}
            floatingLabel
            options={STATUS_TYPE_OPTIONS(t)}
            disabled={isPending}
            data-testid="boards-status-form-type-select"
          />

          {/* WIP limit — manual Controller for number coercion */}
          <Controller
            control={control}
            name="wipLimit"
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="wipLimit">{t('statuses.form.wipLimit')}</FieldLabel>
                <Input
                  {...field}
                  id="wipLimit"
                  type="number"
                  min={1}
                  disabled={isPending}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') {
                      field.onChange(undefined);
                      return;
                    }
                    const parsed = parseInt(v, 10);
                    field.onChange(Number.isNaN(parsed) ? undefined : parsed);
                  }}
                  data-testid="boards-status-form-wiplimit-input"
                />
                <p className="text-xs text-muted-foreground">{t('statuses.form.wipLimitHint')}</p>
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          />
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            type="submit"
            form="board-status-form"
            disabled={isPending}
            data-testid="boards-status-form-submit-button"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending ? t('actions.saving') : t('actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
